// Shared canvas-gallery helpers used by gallery-animate-zoom + wall-gallery.
// All functions are stateless and take the gallery instance as their first
// argument. The galleries keep their class identity; this module just owns
// the texture/tier/eviction/WebGL pipeline that's identical between them.
//
// Per-class contract:
//   gallery.W, gallery.H, gallery.zoom, gallery.panX, gallery.panY
//   gallery.items, gallery._tierPending, gallery._tierLoading
//   gallery._pixiApp, gallery._stats (optional)
//   gallery.thumbUrl(item, tier) — returns the URL for a given tier
//   gallery._itemY(item)         — returns the visual world-Y for the item
//                                   (gallery uses _itemVisualY; wall returns _y)
//   gallery._itemW               — item width in world units (ITEM_W / WALL_W)
//   gallery._wake()              — schedules the RAF tick

import { Texture } from 'pixi.js';

// ── Detection / constants ─────────────────────────────────────────────────────

// iPadOS 13+ reports as MacIntel; maxTouchPoints>1 is the standard escape hatch.
export const IS_IOS = /iPad|iPhone|iPod/.test(navigator.userAgent)
    || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

export const MOBILE_W = 768;
export const MOUNT_BATCH_IOS_MOVING = 2;

// ── "View all" landing view ───────────────────────────────────────────────────
// Entering a gallery with no ?filter= used to land at exactly fit zoom with
// panX/panY 0, which pinned the world's top-left to the viewport's: the row
// labels hang off the left edge of each row, so they sat just off-screen, and
// the top and bottom rows bled past the edges. Landing a touch further out with
// a left inset puts the labels and the whole stack in frame.
export const VIEW_ALL_ZOOM = 0.9; // × fitZ
// Screen px reserved at the left for the labels. Desktop labels render at 16px
// with a 20px gap; phone labels are the same 16px but a much tighter gap.
export const viewAllInsetX = W => (W < MOBILE_W ? 48 : 90);

/**
 * Zoom/pan that frames one collection: as much of its width as will fit, plus
 * the left inset the row label sits in and a matching margin on the right.
 *
 * The floor is the view-all zoom, never lower. Fitting a long collection edge
 * to edge would otherwise zoom further OUT than the whole-archive overview —
 * a 48-look row needs 0.63 × fitZ on a desktop, which shrinks each look to
 * ~30px — and on a phone no collection fits at any usable zoom at all, so every
 * one of them would slam into the zoom floor and look identical. Above that
 * floor, short collections still zoom right in to frame themselves exactly;
 * long ones sit at the overview zoom and pan sideways.
 */
export function fitCollectionView(gallery, group) {
    const inset   = viewAllInsetX(gallery.W);
    const avail   = Math.max(1, gallery.W - inset * 2);
    const floor   = Math.max(gallery.ZOOM_MIN, (gallery.fitZ || 0) * VIEW_ALL_ZOOM);
    const zoom    = Math.min(gallery.ZOOM_MAX, Math.max(floor, avail / group.width));
    return {
        zoom,
        panX: inset,
        panY: gallery.H / 2 - (group.rowY + group.rowH / 2) * zoom,
    };
}

// Cap on number of items holding a unique GPU texture on mobile. Each unique
// texture is its own draw call (Pixi can't batch unique textures), so on mobile
// WebGL — which stalls under high draw-call counts — this is the dominant cost.
// 60 ≈ "items in the unbuffered viewport at floor zoom"; tunable.
export const MAX_CACHED_MOBILE = 90;

// Thumb tiers — must match PocketBase file field thumbs config.
export const TIERS = ['60x0', '300x0', '800x0'];
export const TIER_PX = [60, 300, 800];

export function tierForZoom(zoom, itemW) {
    // iOS: hard-cap at tier 0 (60×90 ≈ 21KB each). Tier 1+ pushed iOS Safari's
    // per-tab memory budget over the edge even with all the other mitigations
    // in place. ~28× less memory than tier 1. Soft thumbnails when zoomed in,
    // but the alternative is "Can't open this page" on initial load.
    if (IS_IOS) return 0;
    const px = itemW * zoom;
    if (px < 80)  return 0;
    if (px < 200) return 1;
    return 2;
}

// ── Deferred texture destroy ──────────────────────────────────────────────────
// Pixi v8's batch pipeline can hold a source reference across frame boundaries,
// so destroying a Texture synchronously (which nulls source.style/_gpuData)
// can cause the next render to throw in _initSource → updateStyle. We queue
// destroys here and flush them after the next renderer.render() call.

export function deferDestroy(gallery, tex) {
    if (!tex) return;
    if (!gallery._pendingDestroy) gallery._pendingDestroy = [];
    gallery._pendingDestroy.push(tex);
}

export function flushDeferredDestroys(gallery) {
    const q = gallery._pendingDestroy;
    if (!q || !q.length) return;
    gallery._pendingDestroy = null;
    for (const t of q) {
        try { t.destroy(true); } catch {}
    }
}

// ── Memory budget ─────────────────────────────────────────────────────────────

export function byteBudget(gallery) {
    if (IS_IOS)                return 40 << 20;
    if (gallery.W < MOBILE_W)  return 60 << 20;
    return 400 << 20;
}

export function bytesOf(item) {
    const t = item._cachedTier;
    if (t < 0 || !item._cachedSrc) return 0;
    const w = TIER_PX[t];
    const aspect = (item.img_h && item.img_w) ? item.img_h / item.img_w : 1.5;
    return w * w * aspect * 4; // RGBA8
}

export function totalCachedBytes(gallery) {
    let total = 0;
    for (const it of gallery.items) total += bytesOf(it);
    return total;
}

// Distance-furthest-outside-viewport-first eviction. Two triggers:
//  - byte budget exceeded (always)
//  - unique-texture count cap exceeded (mobile only — bounds draw-call cost
//    on devices where Pixi can't batch hundreds of unique textures)
//
// Only UNMOUNTED items are evicted. We tried swapping mounted sprites to a
// shared placeholder and destroying their texture, but Pixi v8's batch
// pipeline holds source references across frames and would error on
// `_initSource` reading the nulled `style` of a destroyed source. The
// mount buffer (in updateScene) is the right knob — tighten it on mobile
// so fewer items stay mounted, leaving the cap to act on unmounted items.
export function evictFarTextures(gallery) {
    const budget   = byteBudget(gallery);
    const isMobile = gallery.W < MOBILE_W;
    let total = totalCachedBytes(gallery);
    if (gallery._stats) gallery._stats.bytesMB = Math.round(total / 1048576);

    const withTex = gallery.items.filter(i => i._cachedSrc && !i._el);
    const overBytes = total > budget;
    const overCount = isMobile && withTex.length > MAX_CACHED_MOBILE;
    if (!overBytes && !overCount) return;

    // Viewport rect in world coordinates.
    const vl = -gallery.panX / gallery.zoom;
    const vt = -gallery.panY / gallery.zoom;
    const vr = vl + gallery.W / gallery.zoom;
    const vb = vt + gallery.H / gallery.zoom;
    const itemW = gallery._itemW;
    // Margin beyond the viewport that's protected from eviction. Keeps items
    // around for short pans / oscillations so we don't re-fetch repeatedly
    // (each fetch is a fresh ImageBitmap allocation = native memory churn,
    // which iOS Safari is sensitive to). Tuned at 25% on each axis.
    const marginX = gallery.W / gallery.zoom * 0.25;
    const marginY = gallery.H / gallery.zoom * 0.25;

    const outsideDist = it => {
        const x = it._x;
        const y = gallery._itemY(it);
        const h = it._h;
        // Manhattan overflow from the item rect to the (margin-padded) viewport rect.
        const dx = Math.max(0, (vl - marginX) - (x + itemW), x - (vr + marginX));
        const dy = Math.max(0, (vt - marginY) - (y + h),     y - (vb + marginY));
        return dx + dy; // 0 if inside margin-padded viewport, positive if outside
    };
    withTex.sort((a, b) => outsideDist(b) - outsideDist(a)); // furthest-outside first

    let count = withTex.length;
    for (const item of withTex) {
        const stillOverBytes = total > budget;
        const stillOverCount = isMobile && count > MAX_CACHED_MOBILE;
        if (!stillOverBytes && !stillOverCount) break;
        // Items within the eviction margin (see outsideDist) get distance 0
        // and are protected — they're close enough to viewport that recent or
        // imminent re-mount would otherwise refetch.
        if (outsideDist(item) === 0) break;
        const bytes = bytesOf(item);
        // Defer destroy until after the next render. Pixi v8's batch pipeline
        // can hold source refs across frames; destroying synchronously can
        // cause `_initSource` to read a nulled style on the next render.
        deferDestroy(gallery, item._texture);
        item._texture    = null;
        item._cachedSrc  = null;
        item._cachedTier = -1;
        item._loadedTier = -1;
        total -= bytes;
        count--;
    }
    if (gallery._stats) gallery._stats.bytesMB = Math.round(total / 1048576);
}

// ── Sprite alpha fade-in ──────────────────────────────────────────────────────
// Used by strip-backed desktop mounts: the sprite starts alpha 0 (the strip
// underlay shows the row's real pixels) and fades in when its first real
// texture arrives. Galleries call stepFades(gallery, now) once per RAF tick
// and keep the loop alive while it returns true.

export function registerFade(gallery, sprite, durMs = 250) {
    if (!gallery._fading) gallery._fading = new Map();
    sprite.alpha = 0;
    gallery._fading.set(sprite, { t0: performance.now(), durMs });
    gallery._wake();
}

export function stepFades(gallery, now) {
    const fading = gallery._fading;
    if (!fading || !fading.size) return false;
    for (const [sprite, f] of fading) {
        if (sprite.destroyed) { fading.delete(sprite); continue; }
        const t = (now - f.t0) / f.durMs;
        if (t >= 1) { sprite.alpha = 1; fading.delete(sprite); }
        else sprite.alpha = t;
    }
    return fading.size > 0;
}

// ── Tier-load queue (Map-keyed, dedup by item) ────────────────────────────────

export function ensureTier(gallery, item, tier, dist = 0) {
    if (!item._sprite || item._loadedTier >= tier) return;
    // A load for this tier (or higher) is already IN FLIGHT — _pendingTier is
    // still set but the item is no longer in the queue (drainQueue removed it on
    // start). Skip: don't fire a duplicate. Without this, a slow connection
    // re-queues the same visible look every frame (updateScene runs each tick
    // while loads are pending), firing up to `max` concurrent identical fetches —
    // a load storm that also starves the bandwidth the real images need.
    // (Still-queued items fall through so a pan can update their priority below.)
    if (item._pendingTier >= tier && !gallery._tierPending.has(item.id)) return;
    const cur = gallery._tierPending.get(item.id);
    if (cur && cur.tier >= tier && cur.dist <= dist) return;
    gallery._tierPending.set(item.id, { item, tier, dist });
    item._pendingTier = tier;
    item._pendingDist = dist;
}

export function drainQueue(gallery) {
    if (gallery._destroyed) return;
    // iOS gets the tightest cap to limit peak in-flight ImageBitmap native memory
    // (which iOS Safari can leak even after bitmap.close()).
    const max = IS_IOS ? 2 : (gallery.W < MOBILE_W ? 4 : 16);
    if (!gallery._tierPending.size || gallery._tierLoading >= max) return;
    // Sort once per drain: highest tier first, smallest dist first.
    const entries = [...gallery._tierPending.values()].sort(
        (a, b) => a.tier !== b.tier ? b.tier - a.tier : a.dist - b.dist
    );
    for (const next of entries) {
        if (gallery._tierLoading >= max) break;
        gallery._tierPending.delete(next.item.id);
        if (!next.item._sprite || next.item._loadedTier >= next.tier) continue;
        startTierLoad(gallery, next.item, next.tier);
    }
}

// ── Tier load (fetch → bitmap → texture) ──────────────────────────────────────

// Off-thread WebP decode: fetch → Blob → createImageBitmap (worker) → Texture.
// We do NOT call bitmap.close() — Pixi's GlTextureSystem may re-upload the
// source via texSubImage2D after our render frame (e.g. on style change,
// context state change, or its own internal logic), and an explicitly-closed
// ImageBitmap throws "source data has been detached" when re-bound.
// The bitmap reference is held by source.resource on the TextureSource.
// When we Texture.destroy(true) in evictFarTextures, Pixi releases the source,
// the bitmap becomes unreferenced, and JS GC reclaims native memory.
// Manual close was an optimization for iOS native-memory pressure; the cost
// (broken textures) outweighs the benefit.
export function startTierLoad(gallery, item, tier) {
    if (gallery._destroyed) return;
    const url = gallery.thumbUrl(item, tier);
    if (!url) return;
    // One AbortController per gallery instance; stopTierLoads() aborts it on
    // destroy so swup navigation doesn't keep these fetches alive.
    if (!gallery._loadAbort) gallery._loadAbort = new AbortController();
    const signal  = gallery._loadAbort.signal;
    const prevTex = item._texture;
    const prevUrl = item._cachedSrc;
    gallery._tierLoading++;
    fetch(url, { signal })
        .then(r => { if (!r.ok) throw new Error('HTTP ' + r.status); return r.blob(); })
        .then(blob => createImageBitmap(blob))
        .then(bitmap => {
            // Navigated away mid-flight: drop the result, don't touch the
            // (now destroyed) scene, and don't re-drain the queue.
            if (gallery._destroyed) { gallery._tierLoading--; return; }
            const tex = Texture.from(bitmap);
            item._cachedSrc  = url;
            item._cachedTier = tier;
            item._texture    = tex;
            if (item._sprite) {
                item._sprite.texture = tex;
                item._sprite.tint    = 0xFFFFFF; // clear bh-tint placeholder
                item._loadedTier = tier;
                // Real texture in place — drop the blurhash placeholder so it
                // doesn't sit alongside _texture for the lifetime of the mount.
                if (item._bhTex) { item._bhTex.destroy(true); item._bhTex = null; }
                // Strip-backed mounts start at alpha 0 (strip shows the row's
                // pixels) — fade the first real texture in over it. Tier
                // upgrades (alpha already 1) stay instant.
                if (item._sprite.alpha === 0) registerFade(gallery, item._sprite);
            }
            item._pendingTier = -1;
            gallery._tierLoading--;
            // Free previous tier's GPU texture (and its bitmap). Deferred to
            // post-render to avoid the same Pixi batch-pipeline race that
            // bites mounted-item destroys.
            if (prevTex && prevUrl !== url) deferDestroy(gallery, prevTex);
            drainQueue(gallery);
            gallery._wake();
        })
        .catch(() => {
            item._pendingTier = -1;
            gallery._tierLoading--;
            drainQueue(gallery);
        });
}

// Stop the tier-load pipeline for a gallery being torn down (swup nav). Aborts
// in-flight fetches and clears the pending queue so nothing keeps downloading
// or re-draining after the gallery is gone. Call from the gallery's destroy().
export function stopTierLoads(gallery) {
    gallery._destroyed = true;
    if (gallery._tierPending) gallery._tierPending.clear();
    if (gallery._loadAbort) {
        try { gallery._loadAbort.abort(); } catch {}
        gallery._loadAbort = null;
    }
}

// ── WebGL context loss / restore ──────────────────────────────────────────────

// iOS Safari kills the WebGL context under memory pressure rather than crashing
// the tab. Because we close ImageBitmaps after upload, Pixi can't auto-restore —
// we wipe our cache and let the tier system reload visible items from URL on
// the next render. Mounted sprites get swapped to the singleton placeholder
// FIRST so they don't try to render against the just-destroyed Texture object
// (which would draw black quads — the "canvas black" failure mode).
export function installContextGuard(gallery) {
    gallery._onCtxLost = e => {
        e.preventDefault();
        console.warn('[gallery] WebGL context lost — resetting to placeholders');
        for (const it of gallery.items) {
            if (it._sprite) {
                it._sprite.texture = Texture.WHITE;
                it._sprite.tint    = it._bhTint ?? 0x808080;
            }
            it._texture    = null;
            it._cachedSrc  = null;
            it._cachedTier = -1;
            it._loadedTier = -1;
        }
        // Strip underlays (desktop collections): their textures died with the
        // context — hide them so they don't render against destroyed sources.
        if (gallery.groups) {
            for (const g of gallery.groups) {
                if (g._stripSprite) { g._stripSprite.visible = false; g._stripReady = false; }
            }
        }
    };
    gallery._onCtxRestored = () => {
        console.warn('[gallery] WebGL context restored — reloading visible tiers');
        gallery._reloadStrips?.();
        gallery._wake();
    };
    gallery._pixiApp.canvas.addEventListener('webglcontextlost',     gallery._onCtxLost);
    gallery._pixiApp.canvas.addEventListener('webglcontextrestored', gallery._onCtxRestored);
}

export function removeContextGuard(gallery) {
    if (!gallery._pixiApp?.canvas) return;
    if (gallery._onCtxLost)     gallery._pixiApp.canvas.removeEventListener('webglcontextlost',     gallery._onCtxLost);
    if (gallery._onCtxRestored) gallery._pixiApp.canvas.removeEventListener('webglcontextrestored', gallery._onCtxRestored);
}
